// aiChat.js — global "contextual AI chat overlay" store.
//
// VIN-2026-59 Phase 2 (2026-05-28): the AI Sommelier used to live only
// at the /vinarai route (a full page). To let users ask about whatever
// wine is in front of them — including over the WOTD sheet — we add a
// lightweight chat OVERLAY that can pop on top of any screen. This
// module is the tiny pub/sub that any surface uses to open it:
//
//   import { openWineChat } from '../utils/aiChat.js'
//   openWineChat(wine)   // opens the sheet focused on `wine`
//   openWineChat(null)   // opens in general "ask about my cellar" mode
//
// A single <WineChatOverlay/> mounted in App.jsx subscribes via
// useWineChat() and renders at a z-index above everything (incl. WOTD,
// the bottom nav, and the action sheet). Same module-store pattern as
// overlayState.js + focusedWine — no context provider to thread.

import { useSyncExternalStore } from 'react'

// Stable frozen default so getServerSnapshot / first render don't churn.
const CLOSED = Object.freeze({ open: false, wine: null })

let _state = CLOSED
const listeners = new Set()

function emit() { listeners.forEach(l => l()) }

/**
 * Open the chat overlay. Pass a wine object to focus on it (the AI
 * gets the wine's details + light cellar context), or null/undefined
 * for general cellar-assistant mode.
 *
 * The wine shape is whatever the calling surface has — we read a known
 * set of fields in the overlay (name, producer, vintage, type, region,
 * country, varietal, score/rating, drinkingWindow, notes). Extra fields
 * are ignored.
 */
export function openWineChat(wine = null) {
  _state = { open: true, wine: wine || null }
  emit()
}

export function closeWineChat() {
  // Preserve the last wine so a re-open (e.g. user reopens immediately)
  // can still show context, but mark closed.
  _state = { open: false, wine: _state.wine }
  emit()
}

function subscribe(cb) { listeners.add(cb); return () => listeners.delete(cb) }
function getSnapshot() { return _state }
function getServerSnapshot() { return CLOSED }

export function useWineChat() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
